import { zodResolver } from '@hookform/resolvers/zod';
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { ServiceTitanCustomerQueryParams } from '_lib/api/apiTypes.shim';

import { Box } from '_lib/components/Box/Box';
import { Button } from '_lib/components/Button/Button';
import { Grid } from '_lib/components/Grid/Grid';
import { MenuItem } from '_lib/components/MenuItem/MenuItem';
import { SelectFormItem } from '_lib/components/SelectFormItem/SelectFormItem';
import { Stack } from '_lib/components/Stack/Stack';
import {
  TextFieldFormItem,
  PhoneNumberTextFieldFormItem,
  TextFieldFormItemProps
} from '_lib/components/TextFieldFormItem/TextFieldFormItem';

export type SearchableCustomerQueryParams = Pick<
  ServiceTitanCustomerQueryParams,
  'phoneNumber' | 'name' | 'addressLine1'
>;

export enum CustomerSearchFieldEnum {
  phoneNumber = 'phoneNumber',
  name = 'name',
  addressLine1 = 'addressLine1'
}

const CustomerSearchFormSchema = z.object({
  fieldName: z.nativeEnum(CustomerSearchFieldEnum),
  text: z
    .string({
      required_error: 'Required',
      invalid_type_error: 'Required'
    })
    .min(1, { message: 'Must be 1 or more characters' })
    .max(50, { message: 'Must be 50 or fewer characters' })
});
export type CustomerSearchType = z.infer<typeof CustomerSearchFormSchema>;

type SearchFieldOption = { fieldName: CustomerSearchFieldEnum; label: string };
const searchFieldOptions: SearchFieldOption[] = [
  { fieldName: CustomerSearchFieldEnum.phoneNumber, label: 'Phone' },
  { fieldName: CustomerSearchFieldEnum.name, label: 'Name' },
  { fieldName: CustomerSearchFieldEnum.addressLine1, label: 'Address' }
];

interface SearchTextCache {
  phoneNumber: string;
  name: string;
  addressLine1: string;
}

export interface CustomerSearchProps {
  searchField?: CustomerSearchFieldEnum;
  searchText?: string;
  onCustomerSearch: (customer: CustomerSearchType) => void;
}

const searchFieldNameComponent = (
  fieldName: CustomerSearchFieldEnum,
  props: TextFieldFormItemProps<CustomerSearchType>
) => {
  if (fieldName === CustomerSearchFieldEnum.phoneNumber) return <PhoneNumberTextFieldFormItem {...props} />;
  return <TextFieldFormItem {...props} />;
};

export const CustomerSearch = (props: CustomerSearchProps): React.ReactElement => {
  const { searchField, searchText, onCustomerSearch } = props;

  const [searchTextCache, setSearchTextCache] = useState({
    phoneNumber: (searchField === CustomerSearchFieldEnum.phoneNumber && searchText) || '',
    name: (searchField === CustomerSearchFieldEnum.name && searchText) || '',
    addressLine1: (searchField === CustomerSearchFieldEnum.addressLine1 && searchText) || ''
  });

  const { getValues, handleSubmit, reset, setValue, control } = useForm({
    resolver: zodResolver(CustomerSearchFormSchema),
    defaultValues: {
      fieldName: CustomerSearchFieldEnum.phoneNumber,
      text: ''
    },
    mode: 'onSubmit'
  });

  useEffect(() => {
    if (searchField && searchText !== undefined) {
      const searchFromProps: CustomerSearchType = {
        fieldName: searchField,
        text: searchText
      };
      reset(searchFromProps);
    }
  }, [searchField, searchText, reset]);

  const _submit = handleSubmit(onCustomerSearch);

  const [previousFieldName, setPreviousFieldName] = useState(searchField || CustomerSearchFieldEnum.phoneNumber);
  const onFieldNameUpdate = (newFieldName: CustomerSearchFieldEnum) => {
    const { text } = getValues();

    setSearchTextCache((prevCache) => {
      prevCache[previousFieldName] = text || '';
      return prevCache;
    });
    setPreviousFieldName(newFieldName);

    setValue('text', searchTextCache[newFieldName]);
  };

  const onClear = () => {
    // Get the current field and field index.
    const { fieldName } = getValues();
    const fieldIndex = (fieldName || searchField || CustomerSearchFieldEnum.phoneNumber) as keyof SearchTextCache;

    // Update the search text cache.
    const newSearchTextCache = { ...searchTextCache };
    newSearchTextCache[fieldIndex] = '';
    setSearchTextCache(newSearchTextCache);

    // Update the form state.
    reset({ fieldName, text: '' });
  };

  return (
    <Box>
      <Grid container spacing={1} sx={{ justifyContent: 'center', alignItems: 'center', paddingX: 1, paddingY: 1 }}>
        <Grid item xs={3} sm={3}>
          <SelectFormItem
            name={'fieldName'}
            control={control}
            defaultValue={searchField || CustomerSearchFieldEnum.phoneNumber}
            label='Category'
            required
            variant='outlined'
            onChange={(e) => onFieldNameUpdate(e.target.value as CustomerSearchFieldEnum)}>
            {searchFieldOptions.map((searchField) => (
              <MenuItem key={searchField.fieldName} value={searchField.fieldName}>
                {searchField.label}
              </MenuItem>
            ))}
          </SelectFormItem>
        </Grid>
        <Grid item xs={true} sm={true}>
          {searchFieldNameComponent(getValues('fieldName'), {
            control,
            name: 'text',
            label: 'Search For',
            required: true,
            variant: 'outlined',
            onEnter: _submit
          })}
        </Grid>
        <Grid item xs={'auto'} sm={'auto'}>
          <Stack direction='row' spacing={0.5}>
            <Button variant='outlined' color='error' onClick={onClear} text='Clear' size='small' />
            <Button variant='outlined' color='primary' onClick={_submit} text='Search' size='small' />
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};
